import React, { useCallback, useMemo, memo, useRef, useEffect, useState } from 'react';
import { useThemeColors } from '@brandclub/bc-widgets';
import type { UIMessage } from '../types/mastraTypes';
import { ChatMessage } from './ChatMessage';
import type { ConciergeDevInfo } from '../types/devOptions';
import { buildVoiceSegments } from './voiceSegments';
import { VoiceSegmentSeparatorCard } from './VoiceSegmentSeparatorCard';

const isUIMessage = (msg: any): msg is UIMessage => 'id' in msg && 'parts' in msg && Array.isArray((msg as any).parts);

// Temporarily hide the inline voice-call separator card. Flip back to true to
// restore the "Voice call · duration · N turns / Show N messages" divider.
const SHOW_VOICE_SEGMENT_CARD = false;

function extractTextContent(parts: UIMessage['parts'] | undefined): string {
  if (!parts) return '';
  return parts
    .filter((p): p is { type: 'text'; text: string } => p.type === 'text' && 'text' in p)
    .map((p) => p.text)
    .join('');
}

/**
 * Inline divider component for shared thread context.
 * Rendered as centered text between two horizontal lines.
 */
const SharedThreadDivider: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '4px 0',
      margin: '8px 0',
    }}
  >
    <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(0,0,0,0.12)' }} />
    <span
      style={{
        fontSize: '13px',
        color: 'rgba(0,0,0,0.4)',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
    <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(0,0,0,0.12)' }} />
  </div>
);

interface MessageListV2Props {
  conversationId: string | null;
  messages: UIMessage[];
  showTypingIndicator: boolean;
  streamingAIResponse: string;
  isActivelyStreaming: boolean;
  /** Transient status from AI SDK data parts (e.g. 'thinking' between tool calls). */
  transientStatus?: string | null;
  /** Dev info (devOptions used + effective ranking weights) during active streaming */
  streamingDevInfo?: ConciergeDevInfo | null;
  /** Follow-up questions to display as suggestion pills */
  followUpQuestions?: string[];
  /** Live follow-up suggestions fetched asynchronously after the stream ends. Used for the last assistant message. */
  latestFollowUps?: string[];
  /** True while the async follow-up suggestions fetch is in-flight */
  isLoadingFollowUps?: boolean;
  onSearch: (query: string) => void;
  onUpdateFeedback?: (messageIndex: number, feedback: { type: 'like' | 'dislike'; timestamp: string } | null) => void;
  /** Callback when a product or variant is selected in a chat card - triggers navigation to its PDP */
  onVariantSelect?: (stacklineSku: string) => void;
  /** Callback when a suggestion pill is clicked - populates the input field */
  onSuggestionClick?: (suggestion: string) => void;
  bottomRef: React.RefObject<HTMLDivElement>;
  /** Base URL for API requests. Defaults to '/api-rewards' for landing-page. */
  apiBaseUrl?: string;
  // ---- Sharing state for inline dividers ----
  /** True when viewing a shared thread the current user does not own (pre-fork) */
  isSharedView?: boolean;
  /** Display name of the user who shared this thread */
  sharedByUserName?: string;
  /** The original conversation this forked thread was copied from */
  sourceConversationId?: string;
  /** Message index boundary between copied and new messages in a forked thread */
  sourceCheckpointIndex?: number;
  /** Whether we're polling for a pending assistant response */
  isPendingAssistantResponse?: boolean;
  /** Whether polling has timed out */
  isPollingTimedOut?: boolean;
  /** Retry handler when polling times out */
  onRetry?: () => void;
  /**
   * True while a human teammate is live-handling the thread. When set, the
   * "waiting for AI" dot animation and the polling-timeout retry block are
   * suppressed — the user is talking to a person, not waiting for the bot.
   */
  liveTakeoverActive?: boolean;
  /**
   * Most recent delivery event for the latest user message during live
   * takeover (delivered / failed). Bridges the gap between the per-turn
   * stream ending (which clears `isActivelyStreaming` and would hide the
   * optimistic "Delivering…" pill) and the persisted user message arriving
   * via Mastra refetch (which carries the inline marker on its parts).
   */
  latestUserDelivery?: 'delivered' | 'failed';
  /** Called when the user clicks the Retry button on the last assistant message */
  onRetryLastMessage?: () => void;
  /**
   * Inline error to surface at the bottom of the message list when the
   * per-turn stream fails or the LLM tripwire fires. Replaces the prior toast
   * surface so the error appears in the conversation context, prompting the
   * user to retry from the same place they sent the original message.
   */
  streamErrorMessage?: string | null;
  /** Called when the user clicks the Try-again button on the inline stream error */
  onStreamErrorRetry?: () => void;
  /** Current PDP SKU — when set, product widgets for this SKU render collapsed */
  pdpStacklineSku?: string | null;
  isOnChatWidget?: boolean;
  /** Brand logo URL shown in the share dialog */
  brandLogoUrl?: string;
  /** Open the "Share this conversation" dialog (conversationId, optional messageIndex for share-up-to-this-message) */
  onOpenShareDialog?: (conversationId: string, messageIndex?: number) => void;
  /** 'read-only' when a brand admin is previewing a conversation via viewToken */
  viewMode?: 'read-only';
}

const getTopDividerText = ({
  viewMode,
  isSharedView,
  sharedByUserName,
  sourceConversationId,
}: Pick<MessageListV2Props, 'viewMode' | 'isSharedView' | 'sharedByUserName' | 'sourceConversationId'>):
  | string
  | null => {
  if (viewMode === 'read-only') return null;
  if (isSharedView) return sharedByUserName ? `Shared with you by ${sharedByUserName}` : 'Shared with you';
  if (sourceConversationId) return 'Your copy of a shared conversation';
  return null;
};

export const MessageListV2: React.FC<MessageListV2Props> = memo(
  ({
    conversationId,
    messages,
    showTypingIndicator,
    streamingAIResponse,
    isActivelyStreaming,
    transientStatus,
    streamingDevInfo,
    followUpQuestions = [],
    latestFollowUps = [],
    isLoadingFollowUps = false,
    bottomRef,
    onSearch,
    onUpdateFeedback,
    onVariantSelect,
    onSuggestionClick,
    apiBaseUrl,
    isSharedView,
    sharedByUserName,
    sourceConversationId,
    sourceCheckpointIndex,
    isPendingAssistantResponse,
    isPollingTimedOut,
    onRetry,
    onRetryLastMessage,
    streamErrorMessage,
    onStreamErrorRetry,
    liveTakeoverActive,
    latestUserDelivery,
    pdpStacklineSku,
    isOnChatWidget,
    brandLogoUrl,
    onOpenShareDialog,
    viewMode,
  }) => {
    const listRef = useRef<HTMLDivElement>(null);
    const prevMinHeightElRef = useRef<HTMLElement | null>(null);
    const { themeColors } = useThemeColors();

    // Combine all messages for display
    const displayMessages: any[] = useMemo(
      () => [
        ...messages,
        ...(showTypingIndicator
          ? [
              {
                role: 'assistant' as const,
                content: '',
                timestamp: new Date(),
                isTyping: true,
              },
            ]
          : []),
        // Pending assistant response indicator (polling for backend response).
        // Skipped while a live human teammate has taken over the thread — the
        // user is chatting with a person and is NOT waiting on the AI.
        ...(isPendingAssistantResponse && !isPollingTimedOut && !showTypingIndicator && !liveTakeoverActive
          ? [
              {
                role: 'assistant' as const,
                content: '',
                timestamp: new Date(),
                isTyping: true,
              },
            ]
          : []),
        // Add a streaming message only when streaming text exists.
        ...(streamingAIResponse
          ? [
              {
                role: 'assistant' as const,
                content: streamingAIResponse,
                timestamp: new Date(),
                // Only mark as streaming if we're still actively streaming
                // Once streaming stops, show actions even if we're still showing streamingAIResponse
                isStreaming: isActivelyStreaming,
              },
            ]
          : []),
      ],
      [
        messages,
        showTypingIndicator,
        streamingAIResponse,
        isActivelyStreaming,
        isPendingAssistantResponse,
        isPollingTimedOut,
        liveTakeoverActive,
      ],
    );

    // Track whether the user has actively interacted (streamed/sent) in this conversation session.
    // Resets when displayMessages change length (conversation switch).
    const hasInteractedRef = useRef(false);
    useEffect(() => {
      if (isActivelyStreaming) hasInteractedRef.current = true;
    }, [isActivelyStreaming]);
    useEffect(() => {
      hasInteractedRef.current = false;
    }, [conversationId]);

    useEffect(() => {
      const listEl = listRef.current;
      // Skip the chatGPT-style "scroll user message to top + reserve space
      // below" anchor entirely during live takeover. There's no AI response
      // coming back, so there's no value in pinning the user message to the
      // top of the viewport — the natural inline flow with a "Delivering…"
      // status row underneath is the right behavior.
      if (!listEl || displayMessages.length === 0 || liveTakeoverActive) {
        if (prevMinHeightElRef.current) {
          prevMinHeightElRef.current.style.minHeight = '';
          prevMinHeightElRef.current.style.marginBottom = '';
          prevMinHeightElRef.current = null;
        }
        return;
      }

      const scrollContainer = listEl.closest('[role="log"]') as HTMLElement | null;
      if (!scrollContainer) return;

      // Reserve a viewport-sized minHeight on the last (latest) message so
      // that when use-stick-to-bottom snaps to the bottom after a new turn,
      // the just-sent user message lands ~104px from the top of the viewport
      // (ChatGPT-style initial pose). As the assistant response streams in
      // and exceeds this reserved space, total content height grows and
      // stick-to-bottom naturally follows the bottom — the user message
      // scrolls up out of view, latest text stays pinned at the bottom.
      //
      // We deliberately do NOT manually adjust scrollTop here: that fights
      // use-stick-to-bottom and breaks the "follow bottom once response
      // exceeds the viewport" half of the hybrid behavior.
      const apply = () => {
        const allMessages = listEl.querySelectorAll('.message.user, .message.assistant');
        const lastMsg = allMessages[allMessages.length - 1] as HTMLElement | null;
        if (!lastMsg) return;

        const allUserMessages = listEl.querySelectorAll('.message.user');
        const lastUser = allUserMessages[allUserMessages.length - 1] as HTMLElement | null;

        if (hasInteractedRef.current) {
          const viewportH = scrollContainer.clientHeight;
          const userBubbleH = lastUser ? lastUser.offsetHeight : 0;
          lastMsg.style.marginBottom = '50px';
          lastMsg.style.minHeight = viewportH - userBubbleH - 104 + 'px';
        } else {
          lastMsg.style.minHeight = '';
          lastMsg.style.marginBottom = '50px';
        }

        if (prevMinHeightElRef.current && prevMinHeightElRef.current !== lastMsg) {
          prevMinHeightElRef.current.style.minHeight = '';
          prevMinHeightElRef.current.style.marginBottom = '';
        }
        prevMinHeightElRef.current = lastMsg;
      };

      apply();

      const mo = new MutationObserver(apply);
      mo.observe(listEl, { childList: true });

      const ro = new ResizeObserver(apply);
      ro.observe(scrollContainer);

      return () => {
        mo.disconnect();
        ro.disconnect();
        if (prevMinHeightElRef.current) {
          prevMinHeightElRef.current.style.minHeight = '';
          prevMinHeightElRef.current.style.marginBottom = '';
          prevMinHeightElRef.current = null;
        }
      };
    }, [displayMessages.length, isPollingTimedOut, liveTakeoverActive]);

    // Determine which top divider to show — suppressed entirely in read-only (brand admin preview)
    const topDividerText = getTopDividerText({ viewMode, isSharedView, sharedByUserName, sourceConversationId });

    // Index of the last user message in displayMessages (-1 if none). Used by
    // ChatMessage to drive the optimistic "Delivering…" pending state for the
    // most recently submitted message during live takeover.
    const lastUserMessageIndex = useMemo(() => {
      for (let i = displayMessages.length - 1; i >= 0; i--) {
        if (displayMessages[i].role === 'user') return i;
      }
      return -1;
    }, [displayMessages]);

    // Plan 7b — slice `displayMessages` into a presentation sequence of
    // synthetic voice-segment-header items + real message items. Each
    // message item carries its original `displayIndex` (matching the
    // pre-segmentation position) so the existing `isLastMessage` /
    // `nextUserMessage` / `lastUserMessageIndex` logic continues to work.
    const renderItems = useMemo(() => {
      const items = buildVoiceSegments(displayMessages);
      let cursor = -1;
      return items.map((it) =>
        it.kind === 'voice-segment-header'
          ? it
          : { ...it, displayIndex: ++cursor },
      );
    }, [displayMessages]);

    // Per-conversation membership of "currently hidden" voice segments.
    // The disclose toggle on the separator card adds / removes the
    // segmentKey here; messages whose segmentKey is in the Set are skipped
    // by the renderer below. Cleared when the user switches conversations
    // so segment keys never carry across threads.
    const [hiddenVoiceSegments, setHiddenVoiceSegments] = useState<Set<string>>(() => new Set());
    useEffect(() => {
      setHiddenVoiceSegments(new Set());
    }, [conversationId]);
    const toggleVoiceSegment = useCallback((segmentKey: string) => {
      setHiddenVoiceSegments((prev) => {
        const next = new Set(prev);
        if (next.has(segmentKey)) next.delete(segmentKey);
        else next.add(segmentKey);
        return next;
      });
    }, []);

    return (
      <div
        ref={listRef}
        style={{
          width: '100%',
          maxWidth: '768px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        {/* Top divider for shared/forked conversations */}
        {topDividerText && <SharedThreadDivider text={topDividerText} />}

        {renderItems.map((item) => {
          if (item.kind === 'voice-segment-header') {
            // Temporarily hidden (kept intact for easy re-enable). The voice
            // segment's messages still render inline; only the divider card is
            // suppressed.
            if (!SHOW_VOICE_SEGMENT_CARD) return null;
            const isHidden = hiddenVoiceSegments.has(item.segmentKey);
            return (
              <VoiceSegmentSeparatorCard
                key={`voice-segment-${item.segmentKey}`}
                durationSec={item.durationSec}
                turnCount={item.turnCount}
                tWallStartIso={item.tWallStartIso}
                messageCount={item.messageCount}
                expanded={!isHidden}
                onToggle={() => toggleVoiceSegment(item.segmentKey)}
              />
            );
          }

          const message = item.message;
          const displayIndex = item.displayIndex;

          // Skip messages that belong to a collapsed voice segment.
          if (item.segmentKey && hiddenVoiceSegments.has(item.segmentKey)) return null;

          const hasStreamingMessage = !!streamingAIResponse;
          const isLastMessage =
            displayIndex === messages.length + (showTypingIndicator ? 1 : 0) + (hasStreamingMessage ? 1 : 0) - 1;
          const isTyping = 'isTyping' in message && message.isTyping;
          const isExplicitlyStreaming = 'isStreaming' in message && message.isStreaming;
          const isLastUiMessageStreaming =
            isActivelyStreaming &&
            !isExplicitlyStreaming &&
            isUIMessage(message) &&
            message.role === 'assistant' &&
            displayIndex === messages.length - 1;
          const isStreaming = isExplicitlyStreaming || isLastUiMessageStreaming;

          // Stable key that survives the streaming→complete transition.
          // UIMessages have a permanent `id` — use it so React never remounts the
          // ChatMessage (and any animated child) when isStreaming flips.
          // Synthetic messages (typing indicator, legacy streamingAIResponse overlay)
          // fall back to index-based keys.
          const messageKey = isTyping
            ? 'typing-indicator'
            : isUIMessage(message) && (message as any).id
            ? `msg-${(message as any).id}`
            : `message-${displayIndex}`;

          // Extract content and parts based on message type
          const messageContent = extractTextContent(message.parts);
          const messageUiParts = message.parts as any[];

          // For the last assistant message use the async-fetched latestFollowUps;
          // for older messages fall back to any follow-ups stored in message parts.
          const messageFollowUpQueries: string[] =
            isLastMessage && message.role === 'assistant'
              ? latestFollowUps
              : isUIMessage(message)
              ? message.parts
                  .filter((p: any) => p.type === 'data-follow-ups')
                  .flatMap((p: any) => p.data?.questions ?? [])
              : [];
          // Show suggestions on assistant messages that have follow-up queries and aren't streaming/typing
          const showFollowUpQueries =
            !isTyping && !isStreaming && message.role === 'assistant' && messageFollowUpQueries.length > 0;

          const showFollowUpsLoading =
            isLastMessage &&
            !isTyping &&
            !isStreaming &&
            message.role === 'assistant' &&
            isLoadingFollowUps &&
            latestFollowUps.length === 0;

          // Show fork boundary divider between copied messages and the user's new messages.
          // Uses displayIndex to work both during streaming (messages without messageIndex)
          // and after reload (messages with messageIndex matching their position).
          const showForkBoundary =
            sourceConversationId &&
            sourceCheckpointIndex !== undefined &&
            !isTyping &&
            !isStreaming &&
            displayIndex === sourceCheckpointIndex + 1;

          return (
            <React.Fragment key={messageKey}>
              {showForkBoundary && <SharedThreadDivider text="Your messages below — only visible to you" />}
              <ChatMessage
                bottomRef={isLastMessage ? bottomRef : undefined}
                role={message.role}
                content={messageContent}
                isTyping={isTyping}
                isStreaming={isStreaming}
                isLastMessage={isLastMessage}
                conversationId={conversationId || undefined}
                messageIndex={
                  'messageIndex' in message
                    ? message.messageIndex
                    : !isTyping && !isStreaming
                    ? displayIndex
                    : undefined
                }
                feedback={'feedback' in message ? message.feedback : undefined}
                followUpQueries={showFollowUpQueries ? messageFollowUpQueries : []}
                nextUserMessage={(() => {
                  const nextMsg = messages[displayIndex + 1];
                  if (displayIndex >= messages.length - 1 || nextMsg?.role !== 'user') return undefined;
                  return extractTextContent(nextMsg.parts);
                })()}
                orders={'orders' in message ? message.orders : undefined}
                devInfo={
                  isStreaming && streamingDevInfo
                    ? streamingDevInfo
                    : 'devInfo' in message
                    ? message.devInfo
                    : undefined
                }
                onSearch={onSearch}
                onUpdateFeedback={onUpdateFeedback}
                onVariantSelect={onVariantSelect}
                onSuggestionClick={onSuggestionClick}
                apiBaseUrl={apiBaseUrl}
                isSharedView={isSharedView}
                uiMessageParts={messageUiParts}
                transientStatus={isStreaming ? transientStatus : undefined}
                pdpStacklineSku={pdpStacklineSku}
                isOnChatWidget={isOnChatWidget}
                brandLogoUrl={brandLogoUrl}
                onRetry={
                  // No retry on voice-originated transcripts (item.segmentKey set) —
                  // retry re-runs the turn through the text agent, which doesn't
                  // apply to a finished voice call.
                  isLastMessage && message.role === 'assistant' && !item.segmentKey ? onRetryLastMessage : undefined
                }
                onOpenShareDialog={onOpenShareDialog}
                followUpsLoading={showFollowUpsLoading}
                viewMode={viewMode}
                liveTakeoverActive={liveTakeoverActive}
                isLastUserMessage={message.role === 'user' && displayIndex === lastUserMessageIndex}
                isActivelyStreaming={isActivelyStreaming}
                latestUserDeliveryFromEvents={
                  message.role === 'user' && displayIndex === lastUserMessageIndex ? latestUserDelivery : undefined
                }
              />
            </React.Fragment>
          );
        })}

        {/* Polling timeout: show error and retry button.
            Suppressed during live takeover — see comment on liveTakeoverActive. */}
        {isPollingTimedOut && onRetry && !liveTakeoverActive && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              padding: '24px 16px',
            }}
          >
            <p
              style={{
                fontSize: '14px',
                color: 'rgba(0,0,0,0.5)',
                textAlign: 'center',
                margin: 0,
              }}
            >
              Response generation timed out. Please try again.
            </p>
            <button
              type="button"
              onClick={onRetry}
              style={{
                padding: '8px 24px',
                borderRadius: '20px',
                border: '1px solid rgba(0,0,0,0.2)',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                color: 'rgba(0,0,0,0.7)',
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Per-turn stream / tripwire error: shown inline instead of a toast.
            Left-aligned so it reads as part of the conversation flow next to
            the assistant column. The Try-again button uses the brand theme's
            primary color to match other primary actions in the chat. */}
        {streamErrorMessage && !liveTakeoverActive && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '8px 0',
            }}
          >
            <p
              style={{
                fontSize: '14px',
                color: themeColors.textPrimary || 'rgba(0,0,0,0.7)',
                textAlign: 'left',
                margin: 0,
              }}
            >
              {streamErrorMessage}
            </p>
            {onStreamErrorRetry && (
              <button
                type="button"
                onClick={onStreamErrorRetry}
                style={{
                  padding: '8px 24px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: themeColors.primary || '#181d56',
                  color: themeColors.buttonText || '#ffffff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
);
